import { Select, MultiSelect, TextField, MaterialSymbol, Card, withStyles, TransactionType, Tooltip } from 'vopay';
import { Button, CircleButton, UtilButton, CardCheckbox } from 'UI';
import { useTranslation } from 'react-i18next';
import styleSheet from './ApproversEdit.module.scss';

const cn = withStyles(styleSheet);

export interface PaymentRailGroup {
  id: string;
  paymentRails: TransactionType[];
  amount: string;
  hasNoLimit: boolean;
}

export interface ApproverData {
  username: string;
  paymentRailGroups: PaymentRailGroup[];
}

interface ApproverCardEditProps {
  approver: ApproverData;
  approverIndex: number;
  availableUsers: Array<{ label: string; value: string }>;
  paymentRailOptions: Array<{ value: string; label: string }>;
  errors: Record<string, string>;
  onApproverChange: (username: string) => void;
  onPaymentRailsChange: (groupIndex: number, rails: TransactionType[]) => void;
  onAmountChange: (groupIndex: number, amount: string) => void;
  onNoLimitChange: (groupIndex: number, hasNoLimit: boolean) => void;
  onRemove: () => void;
  onAddPaymentRailGroup: () => void;
  onRemovePaymentRailGroup: (groupIndex: number) => void;
  getAvailablePaymentRails: (groupIndex: number) => Array<{ value: TransactionType; label: string }>;
}

const ApproverCardEdit = ({
  approver,
  approverIndex,
  availableUsers,
  errors,
  onApproverChange,
  onPaymentRailsChange,
  onAmountChange,
  onNoLimitChange,
  onRemove,
  onAddPaymentRailGroup,
  onRemovePaymentRailGroup,
  getAvailablePaymentRails,
}: ApproverCardEditProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Roles.Approvers.Edit',
  });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  return (
    <Card className={cn('approverCard')} hoverEffect={false}>
      <CircleButton
        colorScheme="destructive"
        iconFill
        icon="delete"
        onClick={onRemove}
        size="large"
        aria-label={t('removeButton', { defaultValue: 'Remove' })}
        className={cn('deleteApproverButton')}
      />

      <div className={cn('approverContent')}>
        <div className={cn('railGroupRow')}>
          <div className={cn('formElement')}>
            <label className={cn('fieldLabel')}>{t('approverLabel', { defaultValue: 'Approver' })}</label>
            <Select
              version="2"
              name={`approver-${approverIndex}`}
              placeholder={t('selectUserPlaceholder', { defaultValue: 'Select user' })}
              options={availableUsers}
              value={approver.username}
              filterable
              onChange={(e) => onApproverChange(e.target.value)}
              errorMsg={errors[`approver-${approverIndex}-username`]}
            />
          </div>
        </div>
        <div className={cn('paymentRailGroups')}>
          {approver.paymentRailGroups.map((group, groupIndex) => {
            const availableRails = getAvailablePaymentRails(groupIndex);
            const selectedRails = group.paymentRails;
            const isFirstGroup = groupIndex === 0;

            return (
              <div key={group.id} className={cn('railGroupRow')}>
                <div className={cn('paymentRailColumn')}>
                  {!isFirstGroup && (
                    <CircleButton
                      size="large"
                      colorScheme="destructive"
                      iconFill
                      icon="delete"
                      onClick={() => onRemovePaymentRailGroup(groupIndex)}
                      aria-label={t('removeGroupButton', { defaultValue: 'Remove payment rail group' })}
                      className={cn('deleteRailButton')}
                    />
                  )}

                  <div className={cn('formElement')}>
                    <label className={cn('fieldLabel')}>
                      {t('paymentRailsLabel', { defaultValue: 'Payment Rails' })}

                      <Tooltip
                        position="top"
                        information={t('railsTooltip', {
                          defaultValue:
                            'Only payment rails with an active approval workflow are available for selection',
                        })}
                      />
                    </label>
                    <MultiSelect
                      name={`rails-${approverIndex}-${groupIndex}`}
                      placeholder={t('selectPaymentRailPlaceholder', { defaultValue: 'Select Payment Rail' })}
                      renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
                      options={availableRails}
                      value={selectedRails}
                      onChange={(e) => {
                        const value = e.target.value;
                        onPaymentRailsChange(groupIndex, value === '' ? [] : (value.split(',') as TransactionType[]));
                      }}
                      errorMsg={errors[`approver-${approverIndex}-group-${groupIndex}-rails`]}
                      renderDropdownFooter={() => {
                        const allSelected = availableRails.every((option) => selectedRails.includes(option.value));

                        return (
                          <div className={cn('multiSelectFooter')}>
                            {allSelected ? (
                              <Button
                                onClick={() => {
                                  onPaymentRailsChange(groupIndex, []);
                                }}
                                size="small"
                              >
                                {t('deselectAll', { defaultValue: 'Deselect All' })}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => {
                                  const newSelected = [...selectedRails];
                                  availableRails.forEach((option) => {
                                    if (!newSelected.includes(option.value)) {
                                      newSelected.push(option.value);
                                    }
                                  });
                                  onPaymentRailsChange(groupIndex, newSelected);
                                }}
                                size="small"
                              >
                                {t('selectAll', { defaultValue: 'Select All' })}
                              </Button>
                            )}
                          </div>
                        );
                      }}
                    />

                    {selectedRails.length > 0 && (
                      <div className={cn('selectedRails')}>
                        {selectedRails.map((rail) => (
                          <div key={rail} className={cn('railChip')}>
                            <span className={cn('railChipText')}>
                              {tGlobal(`TransactionTypes.${rail}`, {
                                defaultValue: rail,
                              })}
                            </span>
                            <div
                              className={cn('railChipRemove')}
                              onClick={() => {
                                onPaymentRailsChange(
                                  groupIndex,
                                  selectedRails.filter((r) => r !== rail),
                                );
                              }}
                            >
                              <MaterialSymbol icon="close" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className={cn('formElement')}>
                  <label className={cn('fieldLabel')}>
                    {t('maximumApprovalAmountLabel', { defaultValue: 'Maximum Approval Amount' })}
                  </label>
                  <TextField
                    variant="outlineShadow"
                    preset="currency"
                    name={`amount-${approverIndex}-${groupIndex}`}
                    placeholder={group.hasNoLimit ? t('amountPlaceholder', { defaultValue: 'No Limit' }) : '0.00'}
                    value={group.hasNoLimit ? '' : group.amount}
                    onChange={(e) => onAmountChange(groupIndex, e.target.value)}
                    disabled={group.hasNoLimit}
                    error={errors[`approver-${approverIndex}-group-${groupIndex}-amount`]}
                  />
                </div>

                <div className={cn('formElement')}>
                  <label className={cn('fieldLabel')} aria-hidden="true">
                    &nbsp;
                  </label>
                  <CardCheckbox
                    name={`no-limit-${approverIndex}-${groupIndex}`}
                    checked={group.hasNoLimit}
                    onChange={(e) => onNoLimitChange(groupIndex, e.target.checked ?? false)}
                    label={t('noLimitLabel', { defaultValue: 'Approver has no limit' })}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <UtilButton onClick={onAddPaymentRailGroup} iconLeft="add" size="small">
          {t('addPaymentRailGroupButton', { defaultValue: 'New Maximum Approval Amount' })}
        </UtilButton>
      </div>
    </Card>
  );
};

export default ApproverCardEdit;
